import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Wrench, MapPin, Building2, CalendarDays, Percent, AlertTriangle, IndianRupee } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from "recharts";

const COLORS = { pending: "#B8B8B0", in_progress: "#D19626", blocked: "#C04A2D", completed: "#3A7050" };
const fmt = (n) => `₹${Math.round(Number(n || 0)).toLocaleString("en-IN")}`;

export default function AdminDashboard() {
  const [summary, setSummary] = useState(null);
  const [overdue, setOverdue] = useState(null);
  useEffect(() => {
    api.get("/summary").then((r) => setSummary(r.data));
    api.get("/overdue-summary").then((r) => setOverdue(r.data)).catch(() => {});
  }, []);

  const KPI = ({ label, value, icon: Icon, testid }) => (
    <div className="p-6 rounded-lg border border-border bg-card" data-testid={testid}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">{label}</div>
        <Icon size={16} className="text-primary" />
      </div>
      <div className="heading text-4xl mt-4">{value ?? "—"}</div>
    </div>
  );

  const data = summary?.maintenance
    ? Object.entries(summary.maintenance).map(([k, v]) => ({ name: k.replace("_", " "), value: v, key: k }))
    : [];

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-semibold">Operations overview</div>
      <h1 className="heading text-3xl md:text-4xl mt-1">Estate at a glance</h1>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <KPI label="Projects" value={summary?.projects} icon={Building2} testid="kpi-projects" />
        <KPI label="Plots" value={summary?.plots} icon={MapPin} testid="kpi-plots-admin" />
        <KPI label="Open bookings" value={summary?.bookings_pending} icon={CalendarDays} testid="kpi-bookings-admin" />
        <KPI label="Maintenance open" value={(summary?.maintenance?.pending || 0) + (summary?.maintenance?.in_progress || 0) + (summary?.maintenance?.blocked || 0)} icon={Wrench} testid="kpi-maint-admin" />
      </div>

      {overdue && (overdue.invoice_count > 0 || overdue.owner_count > 0) && (
        <Link to="/payments" className="block mt-6 group" data-testid="overdue-card">
          <div className="p-6 rounded-lg border border-amber-300/60 bg-amber-50/50 dark:bg-amber-950/20 flex items-center justify-between gap-4 group-hover:border-amber-400 transition-colors">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-lg bg-amber-200/60 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300"><AlertTriangle size={20} /></div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-amber-800 dark:text-amber-300">Overdue maintenance</div>
                <div className="heading text-2xl mt-1 flex items-center gap-2"><IndianRupee size={18} className="text-amber-800 dark:text-amber-300" />{fmt(overdue.total).replace("₹","")}</div>
                <div className="text-xs text-amber-800/80 dark:text-amber-300/80 mt-1">
                  <span data-testid="overdue-owner-count">{overdue.owner_count}</span> owner(s) · {overdue.invoice_count} invoice(s)
                  {overdue.oldest_period_end && <> · oldest since <span className="font-medium">{overdue.oldest_period_end}</span></>}
                </div>
              </div>
            </div>
            <span className="text-xs text-amber-900/80 dark:text-amber-300 uppercase tracking-widest group-hover:underline">Review →</span>
          </div>
        </Link>
      )}

      {summary?.sister && (
        <div className="mt-10 p-6 border border-border rounded-lg bg-card">
          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">Sister-estate bookings</div>
              <div className="heading text-xl mt-1">Cross-project cottage stays</div>
            </div>
            <Percent className="text-primary" size={18} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div data-testid="sister-total">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Total</div>
              <div className="heading text-2xl mt-1">{summary.sister.total}</div>
            </div>
            <div data-testid="sister-confirmed">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Confirmed</div>
              <div className="heading text-2xl mt-1">{summary.sister.confirmed}</div>
              <div className="text-[10px] text-muted-foreground mt-1">
                {summary.sister.total > 0 ? `${Math.round((summary.sister.confirmed / summary.sister.total) * 100)}% conversion` : "—"}
              </div>
            </div>
            <div data-testid="sister-revenue">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Revenue</div>
              <div className="heading text-2xl mt-1">₹{Math.round(summary.sister.revenue || 0).toLocaleString("en-IN")}</div>
            </div>
            <div data-testid="sister-discount">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Discount given</div>
              <div className="heading text-2xl mt-1 text-primary">₹{Math.round(summary.sister.discount_value || 0).toLocaleString("en-IN")}</div>
            </div>
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 mt-10">
        <div className="p-6 border border-border rounded-lg bg-card">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">Maintenance breakdown</div>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={data} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80}>
                  {data.map((d) => <Cell key={d.key} fill={COLORS[d.key]} />)}
                </Pie>
                <Tooltip /><Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="p-6 border border-border rounded-lg bg-card">
          <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">Status summary</div>
          <ul className="mt-4 space-y-2 text-sm">
            {data.map((d) => (
              <li key={d.key} className="flex items-center justify-between border-b border-border pb-2">
                <div className="flex items-center gap-2"><span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: COLORS[d.key] }} />{d.name}</div>
                <span className="font-medium">{d.value}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
